<template>
  <div
    class="w-tab-state-manager viewport-full-height"
    :class="activeTabClass"
  >
    <keep-alive
      v-for='(tab, index) of internalTabs'
      :key='index'
    >
      <ValidationObserver
        :ref='tab?.id'
        :class="getTabClass(tab)"
      >
        <slot
          name='tab_content'
          :tab='tab'
          :active-tab='activeTab'
        >
          <slot
            :name='`tab_content_${tab?.id}`'
            :tab='tab'
            class="viewport-full-height"
            :active-tab='activeTab'
          >
            <component
              :is="tab.component"
              v-if="tab.isMounted"
              v-show="tab?.id == activeTab?.id"
              :ref="`tab_${tab?.id}`"
              class="viewport-full-height"
              v-bind="tab.props"
              @tabEvent="e => $emit('tabEvent', e)"
            />
          </slot>
        </slot>
      </ValidationObserver>
    </keep-alive>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { ValidationObserver } from 'vee-validate';
import { registerEventListener } from '@/event-bus/event-bus';
import { mapMutations } from 'vuex';

export default {
  name: 'TabsStateManager',
  components: { ValidationObserver },
  props: {
    tabs: { type: Array, required: true },
    activeTab: { type: Object }
  },
  mixins: [registerEventListener('possibleTabChange', 'possibleTabChange', function (vid) {
    let tabId = this._getTabIdForVid(vid);
    if (tabId == undefined) { return }
    this.$emit('changeTab', tabId);
  })],
  data() {
    return {
      mountedTabIds: [],
      tabStates: {}
    };
  },
  computed: {
    internalTabs() {
      return this.tabs.map(
        (_tab) => ({ ..._tab, isMounted: this._tabContentIsMounted(_tab) })
      );
    },
    activeTabClass() {
      return this.activeTab?.class;
    },
  },
  watch: {
    activeTab: {
      handler(newActiveTab) {
        if (newActiveTab == undefined) { return }
        this._mount(newActiveTab);

        const activeTabRef = this.$refs[newActiveTab.id];
        const activeComponent = Array.isArray(activeTabRef) ? activeTabRef[0] : activeTabRef;

        if (activeComponent && activeComponent?.$el?.nodeType === 1) {
          const hasLookALikeTable = activeComponent.$el.querySelector(
            '.w-base-level-table-look-a-like-unit'
          ) !== null;

          this.setActiveModuleSearchable(hasLookALikeTable);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    for (const tabId in this.$refs) {
      const PROVIDER_INDEX = 0;
      this.$watch(() => this.$refs[tabId][PROVIDER_INDEX].flags.valid,
        valid => this._setStateOfTab(tabId, valid)
      );

      const tab = this.tabs.find(tab => tab.id === tabId);
      if (tab !== undefined && tab.forceInitialMount)
        this._mount(tab);
    }
  },
  methods: {
    ...mapMutations(['setActiveModuleSearchable', 'setActiveModuleName', 'setActiveModuleSubtitle']),
    getTabClass(tab) {
      return tab?.id === this.activeTab?.id
        ? 'viewport-full-height'
        : 'no-height';
    },
    _mount(tab) {
      if (this._tabContentIsMounted(tab) === true) { return }
      this.mountedTabIds.push(tab.id);
    },
    _tabContentIsMounted(tab) {
      return this.mountedTabIds.includes(tab.id);
    },
    _setStateOfTab(tabId, valid) {
      this.tabStates[tabId] = valid ? undefined : 'error'
      this.$emit('setTabstates', cloneDeep(this.tabStates));
    },
    _getTabIdForVid(vid) {
      for (let tab of this.tabs) {
        if (this.$refs[tab.id][0].fields[vid]) {
          return this.$emit('changeTab', tab.id);
        }
      }
    },
    getTabRef(id) {
      return this.$refs[`tab_${id}`][0] || this.$refs[`tab_${id}`];
    }
  }
};
</script>
<style lang="scss" scoped>
  @import "../../../styles/app/reusable-styling";
</style>
